import React, { useRef, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useCommandBar } from 'contexts/command-bar'
import { useGetItemLabel } from '../../utilities'
import { Popover, Actions, EmptyText } from './s'
import { SectionHeader, SectionHeading } from '../s'
import Action from './action'
import SearchInput from 'components/form/input/text'
import Divider from 'components/divider'

const CommandBarActionsPopover = ({ actionsButtonRef }) => {
    const { formatMessage } = useIntl()

    const popoverRef = useRef()
    const actionsRef = useRef()

    const supportsPopoverPositioning = window.CSS.supports('width: anchor-size(width)')

    const {
        // General
        inputRef, actionsInputRef,

        // Commands
        activeItem,
        onKeyDown,

        // Actions
        isShowingActions = false, setIsShowingActions,
        actionsQuery, setActionsQuery,
        actionsItems,
        activeActionsIndex
    } = useCommandBar()

    const getItemLabel = useGetItemLabel()

    useEffect(() => {
        if(popoverRef.current) {
            if(!!isShowingActions) {
                inputRef.current.blur()

                if(supportsPopoverPositioning) {
                    popoverRef.current.showPopover()
                }

                actionsInputRef.current.focus()
            } else {
                inputRef.current.focus()

                if(supportsPopoverPositioning) {
                    popoverRef.current.hidePopover()
                }
            }
        }
    }, [isShowingActions, popoverRef, inputRef, actionsInputRef, supportsPopoverPositioning])

    useEffect(() => {
        const handleClick = e => {
            if(popoverRef.current && !popoverRef.current.contains(e.target) && !actionsButtonRef.current.contains(e.target)) {
                setIsShowingActions(false)
            }
        }

        document.addEventListener('click', handleClick)
        return () => document.removeEventListener('click', handleClick)
    }, [popoverRef, actionsButtonRef, setIsShowingActions])

    useEffect(() => {
        // Scroll active item into view
        if(actionsRef.current) {
            const activeItem = actionsRef.current.querySelector('.active')

            if(activeActionsIndex === 0) {
                actionsRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }

            if(activeItem) {
                activeItem.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                })
            }
        }
    }, [activeActionsIndex])

    const handleOnChange = e => {
        const { value } = e.target
        setActionsQuery(value)
    }

    const filteredActions = actionsItems?.filter(action => {
        if(action.type === 'divider') {
            return true
        }

        if(actionsQuery) {
            return action.label.toLowerCase().includes(actionsQuery.toLowerCase())
        }

        return true
    })

    let actionableIndex = 0

    return (
        <Popover
            id="command-bar-actions"
            {...(supportsPopoverPositioning ?
                { popover: 'manual' }
                : { className: isShowingActions ? 'visible' : 'hidden' }
            )}
            ref={popoverRef}>
            <SectionHeader>
                <SectionHeading>
                    {getItemLabel(activeItem)}
                </SectionHeading>
            </SectionHeader>
            <Actions ref={actionsRef}>
                {filteredActions?.map((action, index) => {
                    if(action.type === 'divider') {
                        if(index === 0 || index === filteredActions.length - 1) {
                            return null
                        }

                        return <Divider key={`divider:${index}`} />
                    }

                    const currentActionableIndex = actionableIndex
                    actionableIndex++

                    return (
                        <Action
                            action={action}
                            index={currentActionableIndex}
                            key={action.actionId} />
                    )
                })}
                {!filteredActions?.filter(action => action.type !== 'divider').length && (
                    <EmptyText>
                        <FormattedMessage
                            id="command_bar_empty"
                            defaultMessage="🤷 Sorry, no results were found." />
                    </EmptyText>
                )}
            </Actions>
            <SearchInput
                placeholder={formatMessage({
                    id: 'action_search',
                    defaultMessage: 'Search'
                })}
                value={actionsQuery}
                onChange={handleOnChange}
                onKeyDown={onKeyDown}
                ref={actionsInputRef} />
        </Popover>
    )
}

export default CommandBarActionsPopover